/**
 * LoadingMessageService
 * 
 * Provides playful, single-word loading messages that rotate during long operations.
 * Messages are categorized by context and include both meaningful and whimsical nonsense words.
 */

// Single-word playful messages
const PLAYFUL_MESSAGES = {
  // Meaningful playful words
  meaningful: [
    "Photosynthesizing...",
    "Tinkering...",
    "Brewing...",
    "Conjuring...",
    "Manifesting...",
    "Orchestrating...",
    "Calibrating...",
    "Harmonizing...",
    "Synthesizing...",
    "Materializing...",
    "Crystallizing...",
    "Percolating...",
    "Assembling...",
    "Weaving...",
    "Sculpting...",
    "Forging...",
    "Channeling...",
    "Summoning...",
    "Transmuting...",
    "Fermenting...",
    "Distilling...",
    "Marinating...",
    "Simmering...",
    "Whisking...",
    "Defragmenting...",
    "Reticulating...",
    "Decoding...",
    "Compiling...",
    "Rendering...",
    "Computing...",
  ],

  // Whimsical nonsense words
  whimsical: [
    "Frobnicating...",
    "Discombobulating...",
    "Bamboozling...",
    "Zigzagging...",
    "Whooshing...",
    "Zippering...",
    "Swizzling...",
    "Doodling...",
    "Whirligagging...",
    "Flibbertigibbing...",
    "Kerfuffling...",
    "Galumphing...",
    "Skedaddling...",
    "Hullaballooing...",
    "Shenaniganing...",
    "Tomfoolering...",
    "Lollygagging...",
    "Rigamaroling...",
    "Brouhaha-ing...",
    "Ballyhoo-ing...",
    "Gobbledegooking...",
    "Wobbulating...",
    "Fluctuating...",
    "Oscillating...",
    "Perambulating...",
  ],

  // Context-specific
  creating: [
    "Crafting...",
    "Assembling...",
    "Building...",
    "Constructing...",
    "Fabricating...",
  ],

  loading: [
    "Fetching...",
    "Retrieving...",
    "Gathering...",
    "Collecting...",
    "Hunting...",
  ],

  saving: [
    "Securing...",
    "Preserving...",
    "Archiving...",
    "Stashing...",
    "Nestling...",
  ],

  processing: [
    "Crunching...",
    "Computing...",
    "Calculating...",
    "Analyzing...",
    "Cogitating...",
  ],

  uploading: [
    "Launching...",
    "Beaming...",
    "Teleporting...",
    "Catapulting...",
    "Rocketing...",
  ],
};

class LoadingMessageService {
  private messages: string[];
  private currentIndex: number;
  private rotationTimer: NodeJS.Timeout | null;

  constructor(contextMessage?: string) {
    this.messages = this.buildMessagePool(contextMessage);
    this.currentIndex = 0;
    this.rotationTimer = null;
    this.shuffleMessages();
  }

  /**
   * Build message pool based on context, mixing meaningful and whimsical
   */
  private buildMessagePool(contextMessage?: string): string[] {
    let contextSpecific: string[] = [];

    if (contextMessage) {
      const lower = contextMessage.toLowerCase();

      if (lower.includes('creat') || lower.includes('mak')) {
        contextSpecific = [...PLAYFUL_MESSAGES.creating];
      } else if (lower.includes('load') || lower.includes('fetch')) {
        contextSpecific = [...PLAYFUL_MESSAGES.loading];
      } else if (lower.includes('sav') || lower.includes('archiv')) {
        contextSpecific = [...PLAYFUL_MESSAGES.saving];
      } else if (lower.includes('process') || lower.includes('comput')) {
        contextSpecific = [...PLAYFUL_MESSAGES.processing];
      } else if (lower.includes('upload') || lower.includes('send')) {
        contextSpecific = [...PLAYFUL_MESSAGES.uploading];
      }
    }

    // Mix: 40% meaningful, 40% whimsical, 20% context-specific
    const pool = [
      ...PLAYFUL_MESSAGES.meaningful.slice(0, 12),
      ...PLAYFUL_MESSAGES.whimsical.slice(0, 12),
      ...contextSpecific.slice(0, 6),
    ];

    return pool.length > 0 ? pool : [...PLAYFUL_MESSAGES.meaningful.slice(0, 10)];
  }

  /**
   * Shuffle messages using Fisher-Yates algorithm
   */
  private shuffleMessages(): void {
    for (let i = this.messages.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.messages[i], this.messages[j]] = [this.messages[j], this.messages[i]];
    }
  }

  /**
   * Get the initial message
   */
  getInitialMessage(): string {
    return this.messages[0];
  }

  /**
   * Get the next message in rotation
   */
  getNextMessage(): string {
    this.currentIndex = (this.currentIndex + 1) % this.messages.length;
    
    // Reshuffle when we complete a cycle
    if (this.currentIndex === 0) {
      this.shuffleMessages();
    }
    
    return this.messages[this.currentIndex];
  }

  /**
   * Start automatic message rotation
   * @param onMessageChange - Callback when message changes
   * @param interval - Rotation interval in milliseconds (default 2500ms)
   * @returns Cleanup function to stop rotation
   */
  startRotation(
    onMessageChange: (message: string, step: number) => void,
    interval: number = 2500
  ): () => void {
    let step = 0;

    this.rotationTimer = setInterval(() => {
      step = (step + 1) % 5; // 5 steps for pulsing circles
      const nextMessage = this.getNextMessage();
      onMessageChange(nextMessage, step);
    }, interval);

    return () => this.stopRotation();
  }

  /**
   * Stop message rotation
   */
  stopRotation(): void {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }
  }
}

export default LoadingMessageService;
